import { useEffect, useRef, useState } from 'react';
import { Button, Space, Toast, Dialog, Input } from 'antd-mobile';
import { LinkOutline, CloseOutline, CheckOutline, ScanCodeOutline } from 'antd-mobile-icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import UserHeader from '../components/UserHeader';
import homeTopImg from '../assets/Ui_Home_Top.png';
import linkBgImg from '../assets/Ui_Home_Link_Bg.png';
import { getMemberQRCode, getExpiredCoupons, getWorkerQRCode, getSpecialCodeCoupon } from '../services/api';
import './ProfilePage.css';

export default function ProfilePage() {
  const { user, logout, initialized } = useAuth();
  const navigate = useNavigate();
  const specialCodeRef = useRef('');
  
  // 调试：检查用户信息
  useEffect(() => {
    if (user) {
      console.log('ProfilePage - User data:', user);
      console.log('ProfilePage - isWorker value:', user.isWorker, 'Type:', typeof user.isWorker);
    }
  }, [user]);
  
  const inviteLink = user?.id ? `https://m.taogo.shop/register?referId=${user.id}` : '';

  const handleCopyLink = async () => {
    if (!inviteLink) {
      Toast.show({ content: 'Invite link is not available' });
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteLink);
        Toast.show({ icon: 'success', content: 'Invite link copied to clipboard!' });
      } else {
        Toast.show({ content: inviteLink });
      }
    } catch (error) {
      Toast.show({ icon: 'fail', content: 'Failed to copy link, please try again' });
    }
  };

  const handleInvite = async () => {
    if (!user?.id) {
      Toast.show({ content: 'Please sign in again to get QR code' });
      return;
    }

    const loading = Toast.show({
      icon: 'loading',
      content: 'Loading QR code...',
      duration: 0,
    });

    try {
      // 获取会员二维码（扫码落地 URL 与「复制邀请链接」一致：referId；后端 qrCodeLink 仅兜底）
      const response = await getMemberQRCode(user.id);
      const qrPayload = inviteLink || response.qrCodeLink;
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrPayload)}`;
      
      loading.close();

      // 检测是否为移动设备
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobile) {
        // 移动端：显示二维码对话框，让用户长按保存
        const dialog = Dialog.show({
          title: 'Invite QR Code',
          content: (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <img 
                src={qrCodeUrl}
                alt="Invite QR Code"
                style={{ width: '250px', height: '250px', margin: '0 auto' }}
              />
              <p style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
                Member Code: {response.memberCode}
              </p>
              <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                Long press the QR code to save or share
              </p>
            </div>
          ),
          closeOnMaskClick: true,
          actions: [
            {
              key: 'close',
              text: 'Close',
              onClick: () => {
                dialog.close();
              },
            },
          ],
        });
      } else {
        // 桌面端：尝试复制到剪贴板
        try {
          if (navigator.clipboard && window.ClipboardItem) {
            const qrResponse = await fetch(qrCodeUrl);
            const blob = await qrResponse.blob();
            const item = new window.ClipboardItem({ [blob.type]: blob });
            await navigator.clipboard.write([item]);
            Toast.show({ icon: 'success', content: 'QR code copied to clipboard!' });
          } else {
            Toast.show({ content: 'Please use MemberQR button to view QR code' });
          }
        } catch (copyError) {
          console.error('Copy failed:', copyError);
          Toast.show({ icon: 'fail', content: 'Failed to copy, please try again' });
        }
      }
    } catch (error) {
      loading.close();
      Toast.show({ icon: 'fail', content: error.message || 'Failed to load QR code' });
    }
  };

  const handleLogout = () => {
    logout();
    Toast.show({ icon: 'success', content: 'Signed out' });
    navigate('/login', { replace: true });
  };

  const handleGetSpecialCoupon = () => {
    if (!user?.id) {
      Toast.show({ content: 'Please sign in again' });
      return;
    }

    specialCodeRef.current = '';

    const dialog = Dialog.show({
      title: 'Get Special Coupon',
      content: (
        <div style={{ padding: '8px 0' }}>
          <Input
            placeholder="Input special code"
            defaultValue=""
            onChange={(value) => {
              specialCodeRef.current = value;
            }}
            onEnterPress={() => {
              dialog.close();
              submitSpecialCode();
            }}
          />
          <p style={{ marginTop: '8px', fontSize: '12px', color: '#999', textAlign: 'left' }}>
            Case insensitive
          </p>
        </div>
      ),
      actions: [
        {
          key: 'cancel',
          text: 'Cancel',
          onClick: () => {
            specialCodeRef.current = '';
            dialog.close();
          },
        },
        {
          key: 'submit',
          text: 'Submit',
          onClick: () => {
            dialog.close();
            submitSpecialCode();
          },
        },
      ],
    });
  };

  const submitSpecialCode = async () => {
    const code = specialCodeRef.current.trim();
    if (!code) {
      Toast.show({ content: 'Please enter a special code' });
      return;
    }

    if (!user?.id) {
      Toast.show({ content: 'Please sign in again' });
      return;
    }

    const toastHandler = Toast.show({ icon: 'loading', content: 'Processing...', duration: 0 });
    try {
      await getSpecialCodeCoupon(user.id, code);
      toastHandler.close();
      Toast.show({ icon: 'success', content: 'Special coupon received successfully!' });
      specialCodeRef.current = '';
    } catch (error) {
      toastHandler.close();
      Toast.show({ icon: 'fail', content: error.message || 'Failed to get special coupon' });
    }
  };

  const handleViewExpiredCoupons = async () => {
    if (!user?.id) {
      Toast.show({ content: 'Please sign in to view expired coupons' });
      return;
    }

    const loading = Toast.show({
      icon: 'loading',
      content: 'Loading...',
      duration: 0,
    });

    try {
      const expiredCoupons = await getExpiredCoupons(user.id);
      loading.close();

      if (!expiredCoupons || expiredCoupons.length === 0) {
        Toast.show({
          icon: 'success',
          content: 'No expired coupons',
        });
        return;
      }

      // 显示过期优惠券列表
      const dialog = Dialog.show({
        title: 'Expired Coupons',
        content: (
          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            {expiredCoupons.map((coupon, index) => {
              const discountAmount = coupon.discountAmount ?? coupon.amount ?? coupon.value;
              const minimumAmount = coupon.minimumAmount ?? coupon.minAmount ?? coupon.thresholdAmount;
              const formattedAmount =
                discountAmount === null || discountAmount === undefined
                  ? '--'
                  : typeof discountAmount === 'number'
                    ? discountAmount.toFixed(discountAmount % 1 === 0 ? 0 : 2)
                    : discountAmount;
              
              return (
                <div
                  key={coupon.id}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    background: '#f5f5f5',
                    borderRadius: '8px',
                    borderLeft: '3px solid #999',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                    {coupon.title || 'Special Offer'}
                  </div>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                    Discount: RM {formattedAmount}
                  </div>
                  {minimumAmount !== null && minimumAmount !== undefined && Number(minimumAmount) > 0 && (
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      Minimum spend: RM {typeof minimumAmount === 'number'
                        ? minimumAmount.toFixed(minimumAmount % 1 === 0 ? 0 : 2)
                        : minimumAmount}
                    </div>
                  )}
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    Expired: {coupon.expireDate || '--'}
                  </div>
                </div>
              );
            })}
          </div>
        ),
        closeOnMaskClick: true,
        actions: [
          {
            key: 'close',
            text: 'Close',
            onClick: () => {
              dialog.close();
            },
          },
        ],
      });
    } catch (error) {
      loading.close();
      Toast.show({
        icon: 'fail',
        content: error.message || 'Failed to load expired coupons',
      });
    }
  };

  const handleShowWorkerQRCode = async () => {
    if (!user?.id) {
      Toast.show({ content: 'Please sign in again to get worker QR code' });
      return;
    }

    const loading = Toast.show({
      icon: 'loading',
      content: 'Loading worker QR code...',
      duration: 0,
    });

    try {
      const response = await getWorkerQRCode(user.id);
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(response.qrCodeLink)}`;
      
      loading.close();

      // 显示员工二维码对话框
      const dialog = Dialog.show({
        title: 'Worker QR Code',
        content: (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <img 
              src={qrCodeUrl}
              alt="Worker QR Code"
              style={{ width: '250px', height: '250px', margin: '0 auto' }}
            />
            <p style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
              Worker Code: {response.workerCode}
            </p>
            <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
              Members can scan this QR code to bind you as their customer service
            </p>
          </div>
        ),
        closeOnMaskClick: true,
        actions: [
          {
            key: 'close',
            text: 'Close',
            onClick: () => {
              dialog.close();
            },
          },
        ],
      });
    } catch (error) {
      loading.close();
      Toast.show({
        icon: 'fail',
        content: error.message || 'Failed to load worker QR code',
      });
    }
  };


  return (
    <div className="profile-page">
      <div className="profile-top-image">
        <img src={homeTopImg} alt="Profile Top" />
      </div>
      <div className="profile-content">
        <UserHeader user={user} />
        <div className="scroll-container">
          <Space direction="vertical" block size={0}>
            <div className="invite-info-card">
              <div className="invite-link-bg">
                <img src={linkBgImg} alt="Link Background" />
                <span className="invite-link-bg-text">Invite Friends</span>
              </div>
              <div className="invite-link-container">
                <div className="invite-link-display">
                  {inviteLink || 'Invite link will appear here after sign-in'}
                </div>
                <Button
                  size="small"
                  color="primary"
                  onClick={handleCopyLink}
                  className="invite-copy-button"
                >
                  Copy
                </Button>
              </div>
              <Button
                block
                color="primary"
                onClick={handleInvite}
                icon={<LinkOutline />}
                className="invite-button"
                style={{ marginTop: '12px' }}
              >
                Invite QR
              </Button>
              
              <div className="invite-description">
                <div className="invite-description-title">
                  Earn RM5 For Every Friend You Invite!
                </div>
                <div className="invite-description-subtitle">How it Works:</div>
                <div className="invite-description-steps">
                  <div className="invite-step">1. Share your invitation link or code with a friend to sign up.</div>
                  <div className="invite-step">2. After they join, their first in-store purchase reaches RM28.</div>
                  <div className="invite-step">3. You'll instantly receive an RM5.</div>
                </div>
                <div className="invite-benefits">
                  <div className="invite-benefit-item">
                    <CheckOutline style={{ color: '#52c41a', fontSize: '16px', marginRight: '8px' }} />
                    <span>No minimum spend - Use it on anything.</span>
                  </div>
                  <div className="invite-benefit-item">
                    <CheckOutline style={{ color: '#52c41a', fontSize: '16px', marginRight: '8px' }} />
                    <span>Stackable - Save even more by using multiple vouchers together</span>
                  </div>
                </div>
              </div>
            </div>
            
            <Space direction="vertical" block size={16}>
              <Button
                block
                color="primary"
                onClick={handleGetSpecialCoupon}
                className="special-coupon-button"
              >
                Get Special Coupon
              </Button>
              {/* 如果是员工，显示员工二维码按钮 */}
              {/* 后端 isWorker 是 Integer 类型，值为 1 表示是员工 */}
              {/* 兼容数字 1 和字符串 "1" */}
              {(user?.isWorker === 1 || user?.isWorker === '1' || Number(user?.isWorker) === 1) && (
                <Button
                  block
                  color="primary"
                  onClick={handleShowWorkerQRCode}
                  icon={<ScanCodeOutline />}
                >
                  Show Worker QR Code
                </Button>
              )}
              <Button
                block
                color="default"
                onClick={handleViewExpiredCoupons}
              >
                View Expired Coupons
              </Button>
              <Button block color="danger" onClick={handleLogout} icon={<CloseOutline />}>
                Sign Out
              </Button>
            </Space>
          </Space>
        </div>
      </div>
    </div>
  );
}
